import { FunctionComponent, useCallback, useMemo } from 'react'

import styled, { css, useTheme } from 'styled-components'

import { desktopCSS } from '@measures/responsive'
import { LIVE_EVENT_DATA_SPORT_TYPES } from '@utils/formatters/common'
import { LiveEventDataSportTypes } from '@utils/formatters/liveEvent'
import useAnimatedPreview from '@hooks/useAnimatedPreview'
import useEnableEnhancedSearchPage, {
  getEnhancedSearchPageEnabled,
} from '@hooks/useEnableEnhancedSearchPage'
import useIsOnSearchPage from '@hooks/useIsOnSearchPage'
import useRefDelegate from '@hooks/useRefDelegate'
import useSportsEvent from '@hooks/useSportsEvent'
import useTracking, { TrackingFnType } from '@hooks/useTracking'
import { stripHtml } from '@hooks/useTracking/utils'
import useViewportTracking from '@hooks/useViewportTracking'
import { CookWidget, JSONTypeForCookWidget } from '@widgets/types'
import HockeySoccerSportsFooter from '@components/SportsFooter/HockeySoccer'
import SportLabelContent from '@components/SportsFooter/SportLabelContent'
import { transformDataForSportsComponents } from '@components/SportsFooter/utils'
import TeaserAnimatedPreview from '@components/TeaserAnimatedPreview'
import BlickPlusLogo from '@components/TeaserListItem/BlickPlusLogo'
import Catchword from '@components/TeaserListItem/Catchword'
import CommercialLabel from '@components/TeaserListItem/CommercialLabel'
import Date from '@components/TeaserListItem/Date'
import Label from '@components/TeaserListItem/Label'
import Lead from '@components/TeaserListItem/Lead'
import PromoLabel from '@components/TeaserListItem/PromoLabel'
import TeaserImage from '@components/TeaserListItem/TeaserImage'
import TeaserWrapper from '@components/TeaserListItem/TeaserWrapper'
import TextAndTimestampWrapper from '@components/TeaserListItem/TextAndTimestampWrapper'
import Title from '@components/TeaserListItem/Title'
import { ListItemTeaserAPIProps } from '@components/TeaserListItem/types'
import VideoLabel from '@components/VideoLabel'

import { SearchResultsData } from './Search/types'

interface StyledTeaserImageProps {
  hasBorder: boolean
}

const StyledTeaserImage = styled(TeaserImage)<StyledTeaserImageProps>`
  ${({
    theme: {
      spacing: { spacing8 },
      colors: { staticWhite },
    },
    hasBorder,
  }) => css`
    ${hasBorder &&
    css`
      &:before {
        content: '';
        position: absolute;
        top: ${spacing8};
        right: ${spacing8};
        bottom: ${spacing8};
        left: ${spacing8};
        border: 1px solid ${staticWhite};
        z-index: 1;
      }
    `}
  `}
`

const StyledVideoLabel = styled(VideoLabel)`
  ${({
    theme: {
      spacing: { spacing16, spacing12 },
    },
  }) => css`
    position: absolute;
    z-index: 1;
    bottom: ${spacing12};
    left: ${spacing12};
    ${desktopCSS(css`
      bottom: ${spacing16};
      left: ${spacing16};
    `)};
  `}
`

const StyledSportsFooter = styled(HockeySoccerSportsFooter)`
  position: absolute;
  bottom: 0;
  left: 0;
`

const ListItemTeaser: FunctionComponent<ListItemTeaserAPIProps> = ({
  accentColor,
  catchword,
  image,
  isPlus,
  labelText,
  lastModifiedDate,
  lead,
  link,
  commercial,
  commercialType,
  targetContentType,
  title,
  video,
  matchId,
  typeOfSport,
  articleId,
  contentOrigin,
  track,
}) => {
  const {
    colors: { fillSport },
  } = useTheme()

  const isValidSport =
    !!matchId &&
    !!typeOfSport &&
    (
      [
        LIVE_EVENT_DATA_SPORT_TYPES.SOCCER,
        LIVE_EVENT_DATA_SPORT_TYPES.HOCKEY,
      ] as LiveEventDataSportTypes[]
    ).includes(typeOfSport)

  const { data: sportsEventData } = useSportsEvent({
    matchId: isValidSport ? matchId : '',
    typeOfSport: isValidSport ? typeOfSport : 'NO_SPORT',
  })

  const sportsData = sportsEventData
    ? transformDataForSportsComponents(sportsEventData)
    : undefined

  const shouldShowSportsComponents =
    sportsData?.typeOfSport === LIVE_EVENT_DATA_SPORT_TYPES.SOCCER ||
    sportsData?.typeOfSport === LIVE_EVENT_DATA_SPORT_TYPES.HOCKEY

  const isCommercial = !!commercialType
  const isEnhancedSearch = useEnableEnhancedSearchPage()
  const isOnSearchPage = useIsOnSearchPage()
  const hideLastBorder = isOnSearchPage && !isEnhancedSearch
  const showCatchword = !!isPlus || !!catchword

  const hasLabelText = !isCommercial && !!labelText

  const labelColor = sportsData?.isLive
    ? undefined
    : isValidSport
      ? fillSport
      : accentColor

  const onImpression = useCallback<TrackingFnType>(
    () => ({
      event: 'teaser_impression',
      eventCategory: video ? 'video_teaser' : 'article_teaser',
      eventLabel: `${articleId}:${stripHtml(title)}`,
      eventAction: 'impression',
      is_branded: !!contentOrigin,
      link_url: link.href,
    }),
    [articleId, title, video, contentOrigin, link.href],
  )

  const onPromoImpression = useCallback<TrackingFnType>(
    () => ({
      event: 'contentcommerce_impression',
      eventCategory: 'BrandStudio',
      eventAction: 'Impression / Brandstudio',
    }),
    [],
  )

  const onClick = useCallback(
    () => ({
      event: 'teaser_click',
      eventCategory: video ? 'video_teaser' : 'article_teaser',
      eventLabel: `${articleId}:${stripHtml(title)}`,
      eventAction: 'click',
      id: articleId,
      is_branded: !!contentOrigin,
      link_url: link.href,
    }),
    [articleId, title, video, contentOrigin, link.href],
  )

  const onSearchResultClick = useCallback<TrackingFnType>(
    ({ queryClient }) => {
      const { totalElements, totalPages, searchResultRange, searchTerm } =
        queryClient.getQueryData<SearchResultsData>(['searchResults']) ?? {}
      const isEnhancedSearch = getEnhancedSearchPageEnabled()

      return isEnhancedSearch
        ? {
            event: 'et_result_click',
            button_label: 'article',
            eventLabel: articleId,
            search_term: searchTerm?.toLowerCase(),
          }
        : {
            event: 'search_result_click',
            eventCategory: 'search',
            eventLabel: articleId,
            eventAction: 'result_click',

            total_elements: totalElements,
            total_pages: totalPages,
            q_search_result_range: searchResultRange,
            search_term: searchTerm?.toLowerCase(),
          }
    },
    [articleId],
  )

  const trackedOnImpression = useTracking(onImpression)
  const trackedOnPromoImpression = useTracking(onPromoImpression)
  const trackedOnClick = useTracking(onClick)
  const trackedOnSearchResultClick = useTracking(onSearchResultClick)

  const finalOnImpression = useCallback(() => {
    trackedOnImpression()
    if (commercialType) {
      trackedOnPromoImpression()
    }
  }, [commercialType, trackedOnImpression, trackedOnPromoImpression])

  const isABtest = !!catchword
  const shouldBeTracked = !!contentOrigin || !!track || isABtest

  const finalOnClick = useCallback(() => {
    if (isOnSearchPage) {
      trackedOnSearchResultClick()
    }
    if (shouldBeTracked) {
      trackedOnClick()
    }
  }, [
    isOnSearchPage,
    shouldBeTracked,
    trackedOnSearchResultClick,
    trackedOnClick,
  ])

  const clickableProps = {
    onClick: finalOnClick,
    ...link,
  }

  const animatedPreviewRef = useAnimatedPreview({
    video,
    articleUrl: clickableProps.href,
  })

  const viewportTrackingRef = useViewportTracking({
    onImpression: finalOnImpression,
    track: shouldBeTracked,
  })

  const ref = useRefDelegate(
    useMemo(
      () => [animatedPreviewRef, viewportTrackingRef],
      [animatedPreviewRef, viewportTrackingRef],
    ),
  )

  const hasBorder = isCommercial && commercialType !== 'editorial'

  return (
    <TeaserWrapper
      clickableProps={clickableProps}
      hideLastBorder={hideLastBorder}
      ref={ref}
    >
      <TextAndTimestampWrapper>
        {isCommercial && commercial?.label && (
          <CommercialLabel
            commercialType={commercialType}
            commercialLogo={commercial.logo}
          >
            {commercial?.label}
          </CommercialLabel>
        )}
        {(hasLabelText || isValidSport) && (
          <Label labelColor={labelColor}>
            {shouldShowSportsComponents ? (
              <SportLabelContent sportsData={sportsData} />
            ) : hasLabelText ? (
              labelText
            ) : null}
          </Label>
        )}
        {showCatchword && (
          <Catchword>
            {isPlus && <BlickPlusLogo />}
            {catchword}
          </Catchword>
        )}
        <Title>{title}</Title>
        {lead ? <Lead>{lead}</Lead> : null}
        {lastModifiedDate ? <Date>{lastModifiedDate}</Date> : null}
      </TextAndTimestampWrapper>
      <StyledTeaserImage {...image} hasBorder={hasBorder}>
        {!!video && (
          <TeaserAnimatedPreview video={video} articleUrl={link?.href} />
        )}
        {commercialType === 'promo' && !!commercial?.label ? (
          <PromoLabel>{commercial.label}</PromoLabel>
        ) : null}
        {!!video && (
          <StyledVideoLabel
            duration={video.duration}
            targetContentType={targetContentType}
          />
        )}
        {shouldShowSportsComponents && (
          <StyledSportsFooter sportsData={sportsData} viewType="compact" />
        )}
      </StyledTeaserImage>
    </TeaserWrapper>
  )
}

const widget = {
  kind: ['teaser-list-item'],
  component: ListItemTeaser,
} as const satisfies CookWidget

export type WidgetType = typeof widget

export type JSONWidgetType = JSONTypeForCookWidget<WidgetType>

export default widget
